import { useEffect, useState, useRef, useCallback } from 'react';
import { useApi } from '../../../utils/hooks';
import { Helpers } from '../../../utils/helpers';
import { debounce } from 'lodash';
import { SortDirection } from '../../../utils/constants/enums';
import { IPagedResult, IUserDto, IUserSearchRequest } from '../../../utils/constants/interfaces';

const emptySearchResults: IPagedResult<IUserDto[]> = {
  currentPage: 1,
  results: [],
  pageSize: 1,
  pageCount: 1,
  resultCount: 0
};

const useUserLookup = (
  userId: string | undefined,
  requireKeywords: boolean,
  onChange: (userId: string) => void
) => {
  const defaultSearchParams: IUserSearchRequest = {
    keywords: '',
    sortDirection: SortDirection.ASCENDING,
    sortField: 'name',
    pageSize: 20,
    page: 1,
    filters: {
      customernumber: '',
      customername: '',
      userid: '',
      email: '',
      permissions: []
    }
  };

  const api = useApi();
  const [loading, setLoading] = useState<boolean>(false);
  const [pageIsReady, setPageIsReady] = useState<boolean>(false);
  const [showSearch, setShowSearch] = useState<boolean>(Helpers.isNullOrWhiteSpace(userId));
  const [userPagedResults, setUserPagedResults] =
    useState<IPagedResult<IUserDto[]>>(emptySearchResults);
  const [searchParameters, setSearchParameters] = useState<IUserSearchRequest>(defaultSearchParams);
  const [showUserQuickAddForm, setShowUserQuickAddForm] = useState<boolean>(false);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);

  const handlePermissionChange = (permission: string, checked: boolean) => {
    const newSelectedPermissions = checked
      ? [...selectedPermissions, permission]
      : selectedPermissions.filter((p) => p !== permission);

    setSelectedPermissions(newSelectedPermissions);

    const newPageSize = newSelectedPermissions.length > 0 ? 1000 : 20;

    setSearchParameters({
      ...searchParameters,
      page: 1,
      pageSize: newPageSize,
      filters: {
        ...searchParameters.filters,
        permissions: []
      }
    });
  };

  const search = useCallback(async (): Promise<void> => {
    if (requireKeywords && Helpers.isNullOrWhiteSpace(searchParameters.keywords)) {
      return;
    }

    setLoading(true);

    const filters = JSON.stringify({
      customernumber: searchParameters.filters.customernumber,
      customername: searchParameters.filters.customername,
      userid: searchParameters.filters.userid,
      email: searchParameters.filters.email
    });
    const queryParams = [];
    queryParams.push(`page=${searchParameters.page}`);
    queryParams.push(`pageSize=${searchParameters.pageSize}`);
    queryParams.push(`sortField=${searchParameters.sortField}`);
    queryParams.push(`sortDirection=${searchParameters.sortDirection.toString()}`);
    queryParams.push(`keywords=${encodeURIComponent(searchParameters.keywords)}`);
    queryParams.push(`filters=${encodeURIComponent(filters)}`);

    const endpoint = `/users?${queryParams.join('&')}`;
    const apiResponse = await api.get<IPagedResult<IUserDto[]>>(endpoint);
    if (apiResponse.isSuccessStatusCode && apiResponse.response) {
      let filteredResults = apiResponse.response;

      if (selectedPermissions.length > 0) {
        filteredResults = {
          ...apiResponse.response,
          results:
            apiResponse.response.results?.filter((user) =>
              selectedPermissions.some((permission) => user.permissions?.includes(permission))
            ) || [],
          resultCount:
            apiResponse.response.results?.filter((user) =>
              selectedPermissions.some((permission) => user.permissions?.includes(permission))
            ).length || 0
        };
      }

      setUserPagedResults(filteredResults);
    } else {
      setUserPagedResults(emptySearchResults);
    }
    setLoading(false);
  }, [api, searchParameters, requireKeywords, selectedPermissions]);

  const resetSearch = () => {
    setSearchParameters(defaultSearchParams);
    setSelectedPermissions([]);
  };

  const changePage = (page: number) => {
    setSearchParameters({ ...searchParameters, page: page });
  };

  const changeSort = (sortField: string) => {
    let sortDirection: SortDirection;
    if (searchParameters.sortField === sortField) {
      // Same field clicked - toggle direction
      sortDirection =
        (searchParameters.sortDirection as SortDirection) === SortDirection.ASCENDING
          ? SortDirection.DESCENDING
          : SortDirection.ASCENDING;
    } else {
      // Different field clicked - start with ascending
      sortDirection = SortDirection.ASCENDING;
    }

    setSearchParameters({
      ...searchParameters,
      sortField: sortField,
      sortDirection: sortDirection
    });
  };

  const selectUser = (userId: string) => {
    setShowSearch(false);
    resetSearch();
    onChange(userId);
  };

  const hasSearchParametersCheck = () => {
    if (requireKeywords && Helpers.isNullOrWhiteSpace(searchParameters.keywords)) return false;

    const hasKeywords = !Helpers.isNullOrWhiteSpace(searchParameters.keywords);
    const hasPermissionFilters = searchParameters.filters.permissions.length > 0;

    return hasKeywords || hasPermissionFilters;
  };

  const debouncedUserSearch = useRef(
    debounce((action: string, value: string) => {
      const localSearchParams = { ...searchParameters };
      localSearchParams.page = 1;
      localSearchParams.keywords = value;
      setSearchParameters(localSearchParams);
    }, 500)
  ).current;

  useEffect(() => {
    return () => debouncedUserSearch.cancel();
  }, [debouncedUserSearch]);

  useEffect(() => {
    void search();
  }, [search, searchParameters]);

  useEffect(() => {
    setPageIsReady(true);
  }, []);

  return {
    pageIsReady,
    loading,
    searchParameters,
    userPagedResults,
    debouncedUserSearch,
    showSearch,
    hasSearchParameters: hasSearchParametersCheck(),
    showUserQuickAddForm,
    selectedPermissions,
    handlePermissionChange,
    setShowSearch,
    selectUser,
    changePage,
    changeSort,
    setShowUserQuickAddForm
  };
};

export default useUserLookup;
